/**
 * Transaction Repository
 * Extends BaseRepository for transaction data access
 */

import { Transaction } from '../domain/types';
import { COLLECTIONS } from '../domain/collections';
import {
    BaseRepository,
    BaseEntity
} from '../domain/services/BaseRepository';
import {
    collection,
    query,
    where,
    orderBy,
    limit as firestoreLimit,
    startAfter,
    getDocs,
    getDoc,
    doc as firestoreDoc,
    addDoc,
    setDoc,
    updateDoc,
    DocumentSnapshot,
    QueryDocumentSnapshot,
    DocumentData,
    QueryConstraint
} from 'firebase/firestore';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';

export interface TransactionEntity extends BaseEntity {
    userId: string;
    type: 'income' | 'expense' | 'refund' | 'topup' | 'withdrawal' | 'payment';
    amount: number;
    currency: string;
    status: 'pending' | 'completed' | 'failed' | 'cancelled';
    description: string;
    method?: string;
    reference?: string;
    orderId?: string;
    partnerId?: string;
    metadata?: Record<string, unknown>;
    timestamp: string;
}

class TransactionRepository extends BaseRepository<TransactionEntity> {
    protected collectionName = COLLECTIONS.TRANSACTIONS;

    protected toEntity(doc: QueryDocumentSnapshot<DocumentData>): TransactionEntity {
        const data = doc.data();
        return {
            id: doc.id,
            userId: (data.userId as string) || '',
            type: (data.type as TransactionEntity['type']) || 'income',
            amount: (data.amount as number) || 0,
            currency: (data.currency as string) || 'ARS',
            status: (data.status as TransactionEntity['status']) || 'pending',
            description: (data.description as string) || '',
            method: data.method as string | undefined,
            reference: data.reference as string | undefined,
            orderId: data.orderId as string | undefined,
            partnerId: data.partnerId as string | undefined,
            metadata: data.metadata as Record<string, unknown> | undefined,
            timestamp: (data.timestamp as string) || new Date().toISOString(),
            createdAt: (data.createdAt as string) || new Date().toISOString(),
            updatedAt: (data.updatedAt as string) || new Date().toISOString()
        };
    }

    protected toDocument(data: Partial<TransactionEntity>): DocumentData {
        const { id, createdAt, ...rest } = data as Record<string, unknown>;
        void id;
        void createdAt;
        return rest as DocumentData;
    }

    async getAllTransactions(lastVisible: DocumentSnapshot | null = null, pageSize: number = 20): Promise<{ transactions: TransactionEntity[], lastDoc: DocumentSnapshot | null }> {
        try {
            const constraints: QueryConstraint[] = [orderBy('timestamp', 'desc'), firestoreLimit(pageSize)];
            if (lastVisible) constraints.push(startAfter(lastVisible));

            const q = query(this.collection, ...constraints);
            const snapshot = await getDocs(q);
            const transactions = snapshot.docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
            const lastDoc = snapshot.docs[snapshot.docs.length - 1] as DocumentSnapshot | null;

            return { transactions, lastDoc };
        } catch (error) {
            logger.error('[TransactionRepository] Error fetching all transactions', error);
            return { transactions: [], lastDoc: null };
        }
    }

    async getUserTransactions(userId: string, lastVisible: DocumentSnapshot | null = null, pageSize: number = 20): Promise<{ transactions: TransactionEntity[], lastDoc: DocumentSnapshot | null }> {
        try {
            if (!userId) return { transactions: [], lastDoc: null };

            const constraints: QueryConstraint[] = [
                where('userId', '==', userId),
                orderBy('timestamp', 'desc'),
                firestoreLimit(pageSize)
            ];
            if (lastVisible) constraints.push(startAfter(lastVisible));

            const q = query(this.collection, ...constraints);
            const snapshot = await getDocs(q);
            const transactions = snapshot.docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
            const lastDoc = snapshot.docs[snapshot.docs.length - 1] as DocumentSnapshot | null;

            return { transactions, lastDoc };
        } catch (error) {
            logger.error(`[TransactionRepository] Error fetching transactions for user ${userId}`, error);
            return { transactions: [], lastDoc: null };
        }
    }

    async getStats(): Promise<{ totalIncome: number, pendingIncome: number }> {
        try {
            const summaryDoc = await getDoc(firestoreDoc(db, 'stats', 'financials'));
            if (summaryDoc.exists()) {
                return summaryDoc.data() as { totalIncome: number, pendingIncome: number };
            }

            const q = query(this.collection, firestoreLimit(200));
            const snapshot = await getDocs(q);
            const transactions = snapshot.docs.map(doc => doc.data() as TransactionEntity);

            const totalIncome = transactions
                .filter(t => (t.type === 'income' || t.type === 'topup') && t.status === 'completed')
                .reduce((sum, t) => sum + t.amount, 0);

            const pendingIncome = transactions
                .filter(t => t.status === 'pending')
                .reduce((sum, t) => sum + t.amount, 0);

            const refunds = transactions
                .filter(t => t.type === 'refund')
                .reduce((sum, t) => sum + Math.abs(t.amount), 0);

            return { totalIncome: totalIncome - refunds, pendingIncome };
        } catch (error) {
            logger.error('[TransactionRepository] Error calculating stats', error);
            return { totalIncome: 0, pendingIncome: 0 };
        }
    }

    async addTransaction(transaction: Omit<TransactionEntity, 'createdAt' | 'updatedAt'>): Promise<string> {
        if ('id' in transaction && transaction.id) {
            await setDoc(firestoreDoc(this.collection, transaction.id), this.toDocument(transaction as TransactionEntity));
            return transaction.id;
        } else {
            const docRef = await addDoc(this.collection, {
                ...this.toDocument(transaction as TransactionEntity),
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            });
            return docRef.id;
        }
    }

    async updateTransactionStatus(id: string, status: 'pending' | 'completed' | 'failed'): Promise<void> {
        try {
            await updateDoc(firestoreDoc(this.collection, id), {
                status,
                updatedAt: new Date().toISOString()
            });
        } catch (error) {
            logger.error(`[TransactionRepository] Error updating transaction status ${id}`, error);
            throw error;
        }
    }
}

export const transactionRepository = new TransactionRepository();
